import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupIcon from "@mui/icons-material/Group";
import StarIcon from "@mui/icons-material/Star";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Colors } from "../../Colors";
import { RootState, useAppDispatch } from "../../store";
import {
  deleteProvider,
  deleteProviderGroup,
  fetchInsurances,
  fetchProviderGroups,
  fetchProviders,
} from "../../slices/ProviderSlice";
import SignoutHeader from "../../subcomponents/SignoutHeader";
import {
  BookableTimeRanges,
  Provider,
  ProviderGroup,
  RawWebptAvailableCalendar,
  TimeRange,
} from "../../types";
import ProviderFormModal from "./components/ProviderFormModal";
import ProviderGroupFormModal from "./components/ProviderGroupFormModal";

const cardSx = {
  backgroundColor: "rgba(255, 255, 255, 0.02)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "12px",
};

const containedButtonSx = {
  textTransform: "none" as const,
  fontSize: "0.875rem",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const tableContainerSx = {
  backgroundColor: "rgba(255, 255, 255, 0.02)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "12px",
  boxShadow: "none",
};

const tableHeadRowSx = {
  "& th": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    color: Colors.info,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    py: 2,
  },
};

const tableBodyRowSx = {
  "& td": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
    py: 1.5,
  },
  "&:last-of-type td": {
    borderBottom: "none",
  },
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.02)",
  },
  transition: "background-color 0.2s ease",
};

const dayOrder: (keyof BookableTimeRanges)[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function formatTimeRanges(ranges: BookableTimeRanges | undefined): string {
  if (!ranges) return "Not set";
  const days = dayOrder.filter((d) => (ranges[d] ?? []).length > 0);
  if (days.length === 0) return "Not set";
  if (days.length === 7) return "All days";
  return `${days.length} day${days.length === 1 ? "" : "s"}`;
}

function summarizeTimeRanges(ranges: TimeRange[] | undefined): string {
  if (!ranges || ranges.length === 0) return "—";
  return ranges
    .filter((r) => r.startTime && r.endTime)
    .map((r) => `${r.startTime}–${r.endTime}`)
    .join(", ");
}

export default function Providers() {
  const dispatch = useAppDispatch();
  const { providers, providerGroups, loading, error, insuranceOptions } =
    useSelector((state: RootState) => state.providers);
  const user = useSelector((state: RootState) => state.auth.user);

  const [webptCalendars, setWebptCalendars] = useState<RawWebptAvailableCalendar[]>([]);

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [activeProvider, setActiveProvider] = useState<Provider | undefined>();

  const [deleteTarget, setDeleteTarget] = useState<Provider | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState<"add" | "edit">("add");
  const [activeGroup, setActiveGroup] = useState<ProviderGroup | undefined>();

  const [deleteGroupTarget, setDeleteGroupTarget] =
    useState<ProviderGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  const [pageError, setPageError] = useState<string>("");

  useEffect(() => {
    if (!user?.token) return;
    dispatch(fetchProviders(user.token));
    dispatch(fetchInsurances(user.token));
    dispatch(fetchProviderGroups(user.token));
  }, [dispatch, user?.token]);

  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/providers/webpt`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        if (!cancelled) setWebptCalendars(response.data || []);
      } catch (err) {
        console.error("Error fetching WebPT calendars:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.token]);

  const webptCalendarMap = useMemo(() => {
    const map = new Map<string, string>();
    webptCalendars.forEach((cal) =>
      map.set(cal.Cal_CalendarID.toString(), cal.Cal_Title)
    );
    return map;
  }, [webptCalendars]);

  const insuranceMap = useMemo(() => {
    const map = new Map<string, string>();
    insuranceOptions.forEach((ins) => map.set(ins.insuranceId, ins.name));
    return map;
  }, [insuranceOptions]);

  const groupsByEhrProviderId = useMemo(() => {
    const map = new Map<string, ProviderGroup[]>();
    providerGroups.forEach((group) => {
      (group.providerIds || []).forEach((ehrId) => {
        const list = map.get(ehrId) || [];
        list.push(group);
        map.set(ehrId, list);
      });
    });
    return map;
  }, [providerGroups]);

  function handleAdd() {
    setActiveProvider(undefined);
    setModalMode("add");
    setModalOpen(true);
  }

  function handleEdit(provider: Provider) {
    setActiveProvider(provider);
    setModalMode("edit");
    setModalOpen(true);
  }

  async function confirmDelete() {
    if (!deleteTarget || !user?.token) return;
    setDeleting(true);
    setPageError("");
    try {
      await dispatch(
        deleteProvider({
          providerId: deleteTarget.providerId,
          token: user.token,
        })
      ).unwrap();
      setSnackbar({ open: true, message: "Provider deleted successfully" });
      setDeleteTarget(null);
    } catch (err) {
      setPageError("Failed to delete provider. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  function handleAddGroup() {
    setActiveGroup(undefined);
    setGroupModalMode("add");
    setGroupModalOpen(true);
  }

  function handleEditGroup(group: ProviderGroup) {
    setActiveGroup(group);
    setGroupModalMode("edit");
    setGroupModalOpen(true);
  }

  async function confirmDeleteGroup() {
    if (!deleteGroupTarget || !user?.token) return;
    setDeletingGroup(true);
    setPageError("");
    try {
      await dispatch(
        deleteProviderGroup({
          providerGroupId: deleteGroupTarget.providerGroupId,
          token: user.token,
        })
      ).unwrap();
      setSnackbar({ open: true, message: "Provider group deleted" });
      setDeleteGroupTarget(null);
    } catch (err) {
      setPageError("Failed to delete provider group. Please try again.");
    } finally {
      setDeletingGroup(false);
    }
  }

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0 },
        color: "#FFF",
        minHeight: "100vh",
        pb: 4,
      }}
    >
      <SignoutHeader />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
          Providers
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.info }}>
          Manage providers and the groups they belong to. Patients only see
          availability for providers in their assigned group.
        </Typography>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, value) => setSelectedTab(value)}
        textColor="primary"
        indicatorColor="primary"
        aria-label="providers and groups"
        sx={{
          mb: 3,
          minHeight: 36,
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          "& .MuiTab-root": {
            textTransform: "none",
            minHeight: 36,
            fontSize: "0.875rem",
            color: Colors.info,
            "&.Mui-selected": { color: Colors.primary },
          },
        }}
      >
        <Tab label="Providers" />
        <Tab label="Groups" />
      </Tabs>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ color: Colors.info }}>
          {selectedTab === 0
            ? `${providers.length} provider${providers.length === 1 ? "" : "s"}`
            : `${providerGroups.length} group${
                providerGroups.length === 1 ? "" : "s"
              }`}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={selectedTab === 0 ? handleAdd : handleAddGroup}
          sx={containedButtonSx}
        >
          {selectedTab === 0 ? "Add Provider" : "Add Group"}
        </Button>
      </Box>

      {(error || pageError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pageError || error}
        </Alert>
      )}

      {selectedTab === 0 && loading && providers.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {selectedTab === 0 && !loading && providers.length === 0 && (
        <Box sx={{ ...cardSx, p: 4 }}>
          <Box sx={{ textAlign: "center", py: 4, color: Colors.info }}>
            <GroupIcon sx={{ fontSize: "48px", opacity: 0.2, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ mb: 1, fontSize: "0.95rem", fontWeight: 600, color: "#FFF" }}
            >
              No providers yet
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.813rem", mb: 3 }}>
              Add your first provider to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={containedButtonSx}
            >
              Add Provider
            </Button>
          </Box>
        </Box>
      )}

      {selectedTab === 0 && providers.length > 0 && (
        <TableContainer component={Paper} sx={tableContainerSx}>
          <Table>
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={{ pl: 3 }}>Provider</TableCell>
                <TableCell>WebPT Calendar</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Max Patients</TableCell>
                <TableCell>Insurances</TableCell>
                <TableCell>Availability</TableCell>
                <TableCell sx={{ textAlign: "right", pr: 3 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.map((provider) => {
                const insuranceCount = (provider.insurancesAccepted || []).length;
                const apptCount = (provider.appointmentTypesAccepted || []).length;
                const insuranceTooltip =
                  insuranceCount === 0
                    ? "No insurances assigned"
                    : (provider.insurancesAccepted || [])
                        .map((id) => insuranceMap.get(id) || id)
                        .join(", ");
                const availabilityTooltip = dayOrder
                  .map((day) => {
                    const summary = summarizeTimeRanges(
                      provider.bookableTimeRanges?.[day]
                    );
                    return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${summary}`;
                  })
                  .join("\n");
                const memberGroups =
                  groupsByEhrProviderId.get(provider.ehrProviderId) || [];
                const groupTooltip = memberGroups
                  .map((g) => {
                    const isPrimary = (g.primaryProviderIds || []).includes(
                      provider.ehrProviderId
                    );
                    return `${g.name || "Untitled"}${
                      isPrimary ? " (primary)" : ""
                    }`;
                  })
                  .join("\n");

                return (
                  <TableRow key={provider.providerId} sx={tableBodyRowSx}>
                    <TableCell sx={{ pl: 3 }}>
                      <Typography
                        sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#FFF" }}
                      >
                        {provider.providerName}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.7rem", color: Colors.info, mt: 0.25 }}
                      >
                        {provider.providerId.slice(0, 8)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <EventAvailableIcon
                          sx={{ fontSize: 16, color: Colors.primary }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            color: webptCalendarMap.has(provider.ehrProviderId)
                              ? Colors.coolWhite
                              : Colors.info,
                            fontStyle: webptCalendarMap.has(provider.ehrProviderId)
                              ? "normal"
                              : "italic",
                          }}
                        >
                          {webptCalendarMap.get(provider.ehrProviderId) ||
                            "Unknown calendar"}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{ fontSize: "0.7rem", color: Colors.info, mt: 0.25 }}
                      >
                        Calendar ID: {provider.ehrProviderId}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {provider.isSupervisingProvider ? (
                        <Chip
                          icon={<VerifiedUserIcon sx={{ fontSize: 14 }} />}
                          label="Supervising"
                          size="small"
                          sx={{
                            backgroundColor: "rgba(0, 172, 79, 0.12)",
                            color: Colors.success,
                            border: "1px solid rgba(0, 172, 79, 0.3)",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            height: "22px",
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: "0.8rem", color: Colors.info }}>
                          Standard
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {memberGroups.length === 0 ? (
                        <Typography
                          sx={{ fontSize: "0.8rem", color: Colors.info }}
                        >
                          —
                        </Typography>
                      ) : (
                        <Tooltip
                          title={
                            <Box
                              sx={{ whiteSpace: "pre-line", fontSize: "0.75rem" }}
                            >
                              {groupTooltip}
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              maxWidth: "200px",
                            }}
                          >
                            {memberGroups.slice(0, 2).map((g) => {
                              const isPrimary = (
                                g.primaryProviderIds || []
                              ).includes(provider.ehrProviderId);
                              return (
                                <Chip
                                  key={g.providerGroupId}
                                  label={g.name || "Untitled"}
                                  size="small"
                                  icon={
                                    isPrimary ? (
                                      <StarIcon sx={{ fontSize: 12 }} />
                                    ) : undefined
                                  }
                                  sx={{
                                    height: "22px",
                                    backgroundColor: isPrimary
                                      ? "rgba(252, 213, 84, 0.12)"
                                      : "rgba(94, 120, 238, 0.12)",
                                    color: isPrimary
                                      ? Colors.warning
                                      : Colors.primary,
                                    border: "none",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    "& .MuiChip-icon": {
                                      color: isPrimary
                                        ? Colors.warning
                                        : Colors.primary,
                                      ml: "6px",
                                      mr: "-4px",
                                    },
                                    "& .MuiChip-label": { px: 1 },
                                  }}
                                />
                              );
                            })}
                            {memberGroups.length > 2 && (
                              <Chip
                                label={`+${memberGroups.length - 2}`}
                                size="small"
                                sx={{
                                  height: "22px",
                                  backgroundColor: "rgba(255, 255, 255, 0.06)",
                                  color: Colors.info,
                                  border: "none",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{ fontSize: "0.85rem", color: Colors.coolWhite }}
                      >
                        {provider.maxSimultaneousPatients ?? "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={insuranceTooltip} placement="top" arrow>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            color:
                              insuranceCount > 0 ? Colors.coolWhite : Colors.info,
                            cursor: "help",
                          }}
                        >
                          {insuranceCount} plan{insuranceCount === 1 ? "" : "s"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={
                          <Box sx={{ whiteSpace: "pre-line", fontSize: "0.75rem" }}>
                            {availabilityTooltip}
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            color: Colors.coolWhite,
                            cursor: "help",
                          }}
                        >
                          {formatTimeRanges(provider.bookableTimeRanges)}
                          {apptCount > 0 && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.7rem",
                                color: Colors.info,
                                ml: 0.75,
                              }}
                            >
                              · {apptCount} appt type{apptCount === 1 ? "" : "s"}
                            </Typography>
                          )}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ pr: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title="Edit provider">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(provider)}
                            sx={{
                              color: Colors.info,
                              "&:hover": { color: Colors.primary },
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete provider">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteTarget(provider)}
                            sx={{
                              color: Colors.info,
                              "&:hover": { color: Colors.error },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedTab === 1 && providerGroups.length === 0 && (
        <Box sx={{ ...cardSx, p: 4 }}>
          <Box sx={{ textAlign: "center", py: 4, color: Colors.info }}>
            <GroupIcon sx={{ fontSize: "48px", opacity: 0.2, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ mb: 1, fontSize: "0.95rem", fontWeight: 600, color: "#FFF" }}
            >
              No provider groups yet
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.813rem", mb: 3 }}>
              Create a group to organize providers and route patients.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddGroup}
              sx={containedButtonSx}
            >
              Add Group
            </Button>
          </Box>
        </Box>
      )}

      {selectedTab === 1 && providerGroups.length > 0 && (
        <TableContainer component={Paper} sx={tableContainerSx}>
          <Table>
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={{ pl: 3 }}>Group</TableCell>
                <TableCell>Providers</TableCell>
                <TableCell>Primary Providers</TableCell>
                <TableCell>Keywords</TableCell>
                <TableCell sx={{ textAlign: "right", pr: 3 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providerGroups.map((group) => {
                const memberNames = (group.providerIds || []).map(
                  (ehrId) =>
                    providers.find((p) => p.ehrProviderId === ehrId)
                      ?.providerName || ehrId
                );
                const primaryNames = (group.primaryProviderIds || []).map(
                  (ehrId) =>
                    providers.find((p) => p.ehrProviderId === ehrId)
                      ?.providerName || ehrId
                );
                const memberTooltip =
                  memberNames.length === 0
                    ? "No providers assigned"
                    : memberNames.join("\n");
                const primaryTooltip =
                  primaryNames.length === 0
                    ? "No primary providers — patients will not be auto-assigned to this group"
                    : primaryNames.join("\n");
                const keywords = group.keyword
                  ? Array.isArray(group.keyword)
                    ? group.keyword
                    : [group.keyword]
                  : [];

                return (
                  <TableRow key={group.providerGroupId} sx={tableBodyRowSx}>
                    <TableCell sx={{ pl: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            backgroundColor: "rgba(94, 120, 238, 0.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <GroupIcon
                            sx={{ color: Colors.primary, fontSize: "18px" }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            color: "#FFF",
                          }}
                        >
                          {group.name || "Untitled group"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={
                          <Box
                            sx={{ whiteSpace: "pre-line", fontSize: "0.75rem" }}
                          >
                            {memberTooltip}
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            color:
                              memberNames.length > 0
                                ? Colors.coolWhite
                                : Colors.info,
                            cursor: "help",
                          }}
                        >
                          {memberNames.length} provider
                          {memberNames.length === 1 ? "" : "s"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={
                          <Box
                            sx={{ whiteSpace: "pre-line", fontSize: "0.75rem" }}
                          >
                            {primaryTooltip}
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        {primaryNames.length === 0 ? (
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              color: Colors.info,
                              cursor: "help",
                            }}
                          >
                            None
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "help",
                            }}
                          >
                            <StarIcon
                              sx={{ fontSize: 14, color: Colors.warning }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: Colors.coolWhite,
                              }}
                            >
                              {primaryNames.length} primary
                            </Typography>
                          </Box>
                        )}
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      {keywords.length === 0 ? (
                        <Typography
                          sx={{ fontSize: "0.8rem", color: Colors.info }}
                        >
                          —
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {keywords.map((kw) => (
                            <Chip
                              key={kw}
                              label={kw}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(94, 120, 238, 0.12)",
                                color: Colors.primary,
                                border: "1px solid rgba(94, 120, 238, 0.3)",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                height: "22px",
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>

                    <TableCell sx={{ pr: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title="Edit group">
                          <IconButton
                            size="small"
                            onClick={() => handleEditGroup(group)}
                            sx={{
                              color: Colors.info,
                              "&:hover": { color: Colors.primary },
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete group">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteGroupTarget(group)}
                            sx={{
                              color: Colors.info,
                              "&:hover": { color: Colors.error },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ProviderFormModal
        open={modalOpen}
        mode={modalMode}
        provider={activeProvider}
        onClose={() => setModalOpen(false)}
        onSuccess={(message) => setSnackbar({ open: true, message })}
      />

      <ProviderGroupFormModal
        open={groupModalOpen}
        mode={groupModalMode}
        group={activeGroup}
        onClose={() => setGroupModalOpen(false)}
        onSuccess={(message) => setSnackbar({ open: true, message })}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? undefined : setDeleteTarget(null))}
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg2,
            color: "#FFF",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete</DialogTitle>
        <DialogContent sx={{ color: Colors.info }}>
          Are you sure you want to delete provider "
          {deleteTarget?.providerName}"? This action cannot be undone.
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ textTransform: "none", color: Colors.info }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteGroupTarget)}
        onClose={() =>
          deletingGroup ? undefined : setDeleteGroupTarget(null)
        }
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg2,
            color: "#FFF",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Provider Group</DialogTitle>
        <DialogContent sx={{ color: Colors.info }}>
          Are you sure you want to delete the group "
          {deleteGroupTarget?.name || "Untitled"}"? Providers in this group are
          not deleted, but patients will no longer be routed by this group.
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteGroupTarget(null)}
            disabled={deletingGroup}
            sx={{ textTransform: "none", color: Colors.info }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteGroup}
            variant="contained"
            color="error"
            disabled={deletingGroup}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {deletingGroup ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />
    </Box>
  );
}
